import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Grid,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Box,
  Stack,
  Chip,
  Divider,
  LinearProgress,
  Skeleton,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Tooltip
} from '@mui/material';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import SpeedOutlinedIcon from '@mui/icons-material/SpeedOutlined';
import PendingActionsOutlinedIcon from '@mui/icons-material/PendingActionsOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import Groups2OutlinedIcon from '@mui/icons-material/Groups2Outlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import TrendingDownOutlinedIcon from '@mui/icons-material/TrendingDownOutlined';
import LineChart from './Charts/LineChart';
import BarChart from './Charts/BarChart';

const numberFormatter = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 1,
});

const formatNumber = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '–';
  if (num === 0) return '0';
  return numberFormatter.format(num);
};

const formatPercent = (value) => {
  if (!Number.isFinite(value)) return '–';
  return `${value > 0 ? '+' : ''}${value.toFixed(1)}%`;
};

const formatShare = (value) => {
  if (!Number.isFinite(value)) return '–';
  return `${value.toFixed(0)}%`;
};

const computeTrend = (series = [], valueKey = 'count') => {
  if (!Array.isArray(series) || series.length < 2) return null;

  const values = series
    .map(item => Number(item?.[valueKey]))
    .filter(val => Number.isFinite(val));

  if (values.length < 2) return null;

  const latest = values[values.length - 1];
  const previous = values[values.length - 2];
  const diff = latest - previous;
  const percent = previous === 0 ? null : (diff / previous) * 100;

  return {
    latest,
    previous,
    diff,
    percent,
    direction: diff >= 0 ? 'up' : 'down',
  };
};

const HighlightCard = ({ title, value, helper, icon: Icon, trend }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Avatar
          variant="rounded"
          sx={{
            bgcolor: theme => theme.palette.mode === 'dark'
              ? 'rgba(94, 53, 177, 0.24)'
              : 'rgba(94, 53, 177, 0.12)',
            color: 'primary.main',
            width: 44,
            height: 44,
          }}
        >
          <Icon />
        </Avatar>
        {trend && (Number.isFinite(trend.percent) || Number.isFinite(trend.diff)) && (
          <Chip
            icon={trend.direction === 'up' ? <TrendingUpOutlinedIcon fontSize="small" /> : <TrendingDownOutlinedIcon fontSize="small" />}
            label={Number.isFinite(trend.percent)
              ? formatPercent(trend.percent)
              : `${trend.direction === 'up' ? '+' : ''}${trend.diff.toLocaleString()}`}
            color={trend.direction === 'up' ? 'success' : 'error'}
            size="small"
            variant="filled"
            sx={{ fontWeight: 600 }}
          />
        )}
      </Stack>
      <Box>
        <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.8 }}>
          {title}
        </Typography>
        <Typography variant="h3" fontWeight={700} gutterBottom>
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {helper}
        </Typography>
      </Box>
    </CardContent>
  </Card>
);

const BacklogItem = ({ label, value, icon: Icon }) => {
  const numericValue = Number(value);
  const hasBacklog = Number.isFinite(numericValue) && numericValue > 0;

  return (
    <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" sx={{ p: 1.5, borderRadius: 2, bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(148, 163, 184, 0.08)' }}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Avatar variant="rounded" sx={{ width: 34, height: 34, bgcolor: hasBacklog ? 'warning.light' : 'success.light', color: hasBacklog ? 'warning.dark' : 'success.dark' }}>
          <Icon fontSize="small" />
        </Avatar>
        <Typography variant="body2" fontWeight={600}>
          {label}
        </Typography>
      </Stack>
      <Chip
        label={formatNumber(value)}
        color={hasBacklog ? 'warning' : 'success'}
        variant="filled"
        size="small"
        sx={{ minWidth: 64 }}
      />
    </Stack>
  );
};

const DistributionRow = ({ label, value, percent }) => (
  <Stack spacing={1} sx={{ pb: 1.5 }}>
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography variant="body2" fontWeight={600}>
        {label}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {formatNumber(value)} · {formatShare(percent)}
      </Typography>
    </Stack>
    <LinearProgress
      variant="determinate"
      value={Math.min(Math.max(percent, 0), 100)}
      sx={{ height: 8, borderRadius: 4 }}
    />
  </Stack>
);

const ChartCard = ({ title, description, children, action }) => (
  <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
    <CardHeader
      title={title}
      subheader={description}
      action={action}
      titleTypographyProps={{ variant: 'h6', fontWeight: 700 }}
    />
    <Divider />
    <CardContent sx={{ flexGrow: 1, px: { xs: 2, md: 3 }, pb: 3 }}>
      <Box sx={{ height: '100%' }}>{children}</Box>
    </CardContent>
  </Card>
);

const AdminOverview = ({
  kpis,
  moreKpis,
  moreKpisLoading,
  moreKpisError,
  activityLog,
  activityLogLoading,
  activityLogError,
  chartData = {},
}) => {
  const engagement = useMemo(() => {
    const participants = Number(kpis?.currentParticipants);
    const observers = Number(kpis?.currentObservers);
    const totalUsers = Number(kpis?.users);
    const liveTotal = [participants, observers].filter(Number.isFinite).reduce((sum, val) => sum + val, 0);
    const rate = Number.isFinite(totalUsers) && totalUsers > 0 ? (liveTotal / totalUsers) * 100 : null;

    return {
      participants,
      observers,
      liveTotal,
      totalUsers,
      rate,
    };
  }, [kpis]);

  const userActivityTrend = useMemo(() => computeTrend(chartData?.userActivity), [chartData?.userActivity]);
  const auctionsTrend = useMemo(() => computeTrend(chartData?.auctionsCreated), [chartData?.auctionsCreated]);
  const productsTrend = useMemo(() => computeTrend(chartData?.productsAdded), [chartData?.productsAdded]);

  const highlightCards = useMemo(() => ([
    {
      title: 'Platform Users',
      value: formatNumber(kpis?.users),
      helper: `${formatNumber(kpis?.onlineUsers)} online now`,
      icon: PeopleAltOutlinedIcon,
      trend: userActivityTrend,
    },
    {
      title: 'Active Auctions',
      value: formatNumber(moreKpis?.activeAuctions ?? kpis?.auctions),
      helper: `${formatNumber(kpis?.pendingSecondHandAuctions)} awaiting launch`,
      icon: GavelOutlinedIcon,
      trend: auctionsTrend,
    },
    {
      title: 'Catalog Size',
      value: formatNumber(moreKpis?.products),
      helper: `${formatNumber(productsTrend?.latest)} items added today`,
      icon: Inventory2OutlinedIcon,
      trend: productsTrend,
    },
    {
      title: 'Engagement Rate',
      value: engagement.rate != null ? formatPercent(engagement.rate) : '–',
      helper: `${formatNumber(engagement.liveTotal)} users active in rooms`,
      icon: SpeedOutlinedIcon,
      trend: null,
    },
  ]), [kpis, moreKpis, engagement, userActivityTrend, auctionsTrend, productsTrend]);

  const backlogItems = useMemo(() => ([
    {
      label: 'Second-hand auctions awaiting review',
      value: kpis?.pendingSecondHandAuctions,
      icon: PendingActionsOutlinedIcon,
    },
    {
      label: 'Product approvals pending',
      value: kpis?.pendingProductApprovals,
      icon: FactCheckOutlinedIcon,
    },
    {
      label: 'Bids pending validation',
      value: kpis?.pendingSecondHandBids,
      icon: WarningAmberOutlinedIcon,
    },
  ]), [kpis]);

  const roleDistribution = useMemo(() => {
    const entries = Array.isArray(chartData?.roleDistribution) ? chartData.roleDistribution : [];
    const total = entries
      .map(item => Number(item?.count))
      .filter(Number.isFinite)
      .reduce((sum, val) => sum + val, 0);

    return entries.map(item => {
      const value = Number(item?.count);
      const percent = Number.isFinite(value) && total > 0 ? (value / total) * 100 : 0;
      return {
        role: item?.role || 'Unknown',
        count: value,
        percent,
      };
    });
  }, [chartData?.roleDistribution]);

  const latestActivityDate = useMemo(() => {
    if (!Array.isArray(chartData?.userActivity) || !chartData.userActivity.length) return null;
    return chartData.userActivity[chartData.userActivity.length - 1]?.date;
  }, [chartData?.userActivity]);

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Grid container spacing={3}>
          {highlightCards.map((card, idx) => (
            <Grid item xs={12} sm={6} lg={3} key={`${card.title}-${idx}`}>
              <HighlightCard {...card} />
            </Grid>
          ))}
        </Grid>
      </Grid>

      <Grid item xs={12} lg={8}>
        <ChartCard
          title="Engagement last 7 days"
          description={latestActivityDate ? `Updated ${latestActivityDate}` : 'Live updates'}
          action={
            userActivityTrend && Number.isFinite(userActivityTrend.diff) ? (
              <Tooltip title="change vs. previous day">
                <Chip
                  icon={userActivityTrend.direction === 'up' ? <TrendingUpOutlinedIcon fontSize="small" /> : <TrendingDownOutlinedIcon fontSize="small" />}
                  label={`${userActivityTrend.direction === 'up' ? '+' : ''}${userActivityTrend.diff.toLocaleString()} events`}
                  color={userActivityTrend.direction === 'up' ? 'success' : 'error'}
                  size="small"
                />
              </Tooltip>
            ) : null
          }
        >
          <LineChart
            data={chartData?.userActivity || []}
            labelKey="date"
            valueKey="count"
            seriesName="Engagement events"
            color="#5E35B1"
            height={300}
          />
        </ChartCard>
      </Grid>

      <Grid item xs={12} lg={4}>
        <Stack spacing={3} height="100%">
          <Card sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <CardHeader
              title="Operational backlog"
              subheader="Items that require administrator attention"
              titleTypographyProps={{ variant: 'h6', fontWeight: 700 }}
            />
            <Divider />
            <CardContent sx={{ flexGrow: 1 }}>
              <Stack spacing={1.5}>
                {backlogItems.map(item => (
                  <BacklogItem key={item.label} {...item} />
                ))}
              </Stack>
            </CardContent>
          </Card>

          <Card sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <CardHeader
              title="Live room snapshot"
              subheader="Real-time load across active auctions"
              titleTypographyProps={{ variant: 'h6', fontWeight: 700 }}
            />
            <Divider />
            <CardContent>
              <Stack spacing={2}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Stack spacing={0.5}>
                    <Typography variant="body2" fontWeight={600}>Participants</Typography>
                    <Typography variant="h5" fontWeight={700}>{formatNumber(engagement.participants)}</Typography>
                  </Stack>
                  <Chip icon={<Groups2OutlinedIcon fontSize="small" />} label="Active" color="primary" size="small" />
                </Stack>
                <LinearProgress variant="determinate" value={engagement.rate != null ? Math.min(engagement.rate, 100) : 0} sx={{ height: 10, borderRadius: 5 }} />
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Stack spacing={0.5}>
                    <Typography variant="body2" fontWeight={600}>Observers</Typography>
                    <Typography variant="h6" fontWeight={600}>{formatNumber(engagement.observers)}</Typography>
                  </Stack>
                  <Chip icon={<VisibilityOutlinedIcon fontSize="small" />} label="Watching" color="info" size="small" />
                </Stack>
                <Divider flexItem sx={{ my: 1 }} />
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">Auctions live right now</Typography>
                  <Typography variant="body1" fontWeight={700}>{formatNumber(moreKpis?.activeAuctions ?? kpis?.auctions)}</Typography>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      </Grid>

      <Grid item xs={12} lg={4}>
        <ChartCard
          title="Auctions created (7 days)"
          description="New auctions opened each day"
        >
          <BarChart
            data={chartData?.auctionsCreated || []}
            labelKey="date"
            valueKey="count"
            seriesName="Auctions"
            color="#26A69A"
            height={280}
          />
        </ChartCard>
      </Grid>

      <Grid item xs={12} lg={4}>
        <ChartCard
          title="Products added (7 days)"
          description="New catalog entries awaiting approval"
        >
          <BarChart
            data={chartData?.productsAdded || []}
            labelKey="date"
            valueKey="count"
            seriesName="Products"
            color="#7E57C2"
            height={280}
          />
        </ChartCard>
      </Grid>

      <Grid item xs={12} lg={4}>
        <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <CardHeader
            title="Role distribution"
            subheader="User roles that generated activity"
            titleTypographyProps={{ variant: 'h6', fontWeight: 700 }}
          />
          <Divider />
          <CardContent sx={{ flexGrow: 1 }}>
            {roleDistribution.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No role activity captured yet.</Typography>
            ) : (
              <Stack spacing={1.5}>
                {roleDistribution.map(item => (
                  <DistributionRow
                    key={item.role}
                    label={item.role}
                    value={item.count}
                    percent={item.percent}
                  />
                ))}
              </Stack>
            )}
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} lg={6}>
        <ChartCard
          title="Live room mix"
          description="Participants vs observers right now"
        >
          <BarChart
            data={chartData?.liveRoles || []}
            labelKey="role"
            valueKey="count"
            seriesName="Live users"
            color="#42A5F5"
            height={260}
            horizontal
          />
        </ChartCard>
      </Grid>

      <Grid item xs={12} lg={6}>
        <ChartCard
          title="Revenue-ready transactions"
          description="Completed checkouts logged in the last refresh"
          action={
            moreKpisLoading ? <LinearProgress variant="indeterminate" sx={{ width: 120 }} /> : null
          }
        >
          <Stack spacing={2} justifyContent="center" sx={{ height: '100%' }}>
            <Typography variant="h2" fontWeight={700} textAlign="center">
              {formatNumber(moreKpis?.transactions)}
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Transactions processed today
            </Typography>
            <Divider flexItem sx={{ my: 1 }} />
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">Reviews received</Typography>
              <Typography variant="body1" fontWeight={600}>{formatNumber(moreKpis?.reviews)}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">Products pending approval</Typography>
              <Typography variant="body1" fontWeight={600}>{formatNumber(kpis?.pendingProductApprovals)}</Typography>
            </Stack>
          </Stack>
        </ChartCard>
      </Grid>

      <Grid item xs={12}>
        <Card sx={{ display: 'flex', flexDirection: 'column' }}>
          <CardHeader
            title="Recent activity"
            subheader="Latest actions across auctions, products, and users"
            titleTypographyProps={{ variant: 'h6', fontWeight: 700 }}
          />
          <Divider />
          <CardContent>
            {activityLogLoading ? (
              <Stack spacing={1.5}>
                {Array.from({ length: 7 }).map((_, idx) => (
                  <Skeleton key={idx} variant="rounded" height={52} />
                ))}
              </Stack>
            ) : activityLogError ? (
              <Alert severity="error" variant="outlined">
                {activityLogError}
              </Alert>
            ) : (
              <List sx={{ maxHeight: 360, overflowY: 'auto' }}>
                {activityLog?.length ? (
                  activityLog.map((log, index) => (
                    <ListItem key={`${log?._id || log?.message || 'log'}-${index}`} divider disableGutters sx={{ py: 1.25 }}>
                      <ListItemIcon sx={{ minWidth: 44 }}>
                        <TimelineOutlinedIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body2" fontWeight={600}>
                            {log?.message || 'Activity recorded'}
                          </Typography>
                        }
                        secondary={log?.createdAt ? new Date(log.createdAt).toLocaleString() : '—'}
                      />
                    </ListItem>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No activity entries yet. Keep an eye here for system events.
                  </Typography>
                )}
              </List>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

AdminOverview.propTypes = {
  kpis: PropTypes.object,
  moreKpis: PropTypes.object,
  moreKpisLoading: PropTypes.bool,
  moreKpisError: PropTypes.string,
  activityLog: PropTypes.array,
  activityLogLoading: PropTypes.bool,
  activityLogError: PropTypes.string,
  chartData: PropTypes.object,
};

export default AdminOverview;